import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { config } from '../Configs';
import { generateMockToken } from '../Utils/Captcha/generateMockToken';

const CAPTCHA_DISABLED = config.turnstile.disableValidation;

const getInitialToken = () => {
  if (CAPTCHA_DISABLED) {
    return generateMockToken();
  }
  return null;
};

export const useCaptcha = () => {
  const [captchaToken, setCaptchaToken] = useState(getInitialToken);
  const [loading, setLoading] = useState(!CAPTCHA_DISABLED);

  const captchaRef = useRef(null);
  const timeoutRef = useRef(null);
  const captchaTokenRef = useRef(null);
  const pendingResolverRef = useRef(null);
  const pendingPromiseRef = useRef(null);
  const readyResolverRef = useRef(null);
  const readyPromiseRef = useRef(null);
  const isReadyRef = useRef(false);

  const resolveReady = useCallback(value => {
    if (!readyResolverRef.current) {
      return;
    }

    const resolve = readyResolverRef.current;
    readyResolverRef.current = null;
    readyPromiseRef.current = null;
    resolve(value);
  }, []);

  const resolvePending = useCallback(value => {
    if (!pendingResolverRef.current) {
      return;
    }

    const { resolve, timeoutId } = pendingResolverRef.current;
    clearTimeout(timeoutId);
    pendingResolverRef.current = null;
    pendingPromiseRef.current = null;
    resolve(value);
  }, []);

  const onResolved = useCallback(
    token => {
      captchaTokenRef.current = token;
      setCaptchaToken(token);
      resolvePending(token);
    },
    [resolvePending]
  );

  const onExpired = useCallback(() => {
    captchaTokenRef.current = null;
    setCaptchaToken(null);
    resolvePending(null);
  }, [resolvePending]);

  const onError = useCallback(() => {
    captchaTokenRef.current = null;
    setCaptchaToken(null);
    resolvePending(null);
  }, [resolvePending]);

  const onLoaded = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    isReadyRef.current = true;
    setLoading(false);
    resolveReady(captchaRef.current);
  }, [resolveReady]);

  const reset = useCallback(() => {
    const api = captchaRef.current;
    if (api && typeof api.reset === 'function') {
      api.reset();
    }

    if (CAPTCHA_DISABLED) {
      return true;
    }

    captchaTokenRef.current = null;
    setCaptchaToken(null);
  }, []);

  const waitForCaptchaReady = useCallback(timeoutMs => {
    const api = captchaRef.current;
    if (isReadyRef.current && api && typeof api.execute === 'function') {
      return Promise.resolve(api);
    }

    if (!readyPromiseRef.current) {
      readyPromiseRef.current = new Promise(resolve => {
        readyResolverRef.current = resolve;
      });
    }

    return Promise.race([
      readyPromiseRef.current.then(readyApi => {
        if (readyApi && typeof readyApi.execute === 'function') {
          return readyApi;
        }

        return null;
      }),
      new Promise(resolve => {
        const timeoutId = setTimeout(() => {
          resolve(null);
        }, timeoutMs);

        readyPromiseRef.current.finally(() => {
          clearTimeout(timeoutId);
        });
      })
    ]);
  }, []);

  const getToken = useCallback(
    async (timeoutMs = 10000) => {
      if (CAPTCHA_DISABLED) {
        return generateMockToken();
      }

      if (pendingPromiseRef.current) {
        return pendingPromiseRef.current;
      }

      const captchaApi = await waitForCaptchaReady(timeoutMs);
      if (!captchaApi) {
        return null;
      }

      captchaTokenRef.current = null;
      setCaptchaToken(null);

      const tokenPromise = new Promise(resolve => {
        const timeoutId = setTimeout(() => {
          if (typeof captchaApi.reset === 'function') {
            captchaApi.reset();
          }

          captchaTokenRef.current = null;
          setCaptchaToken(null);
          pendingResolverRef.current = null;
          pendingPromiseRef.current = null;
          resolve(null);
        }, timeoutMs);

        pendingResolverRef.current = { resolve, timeoutId };

        try {
          captchaApi.execute();
        } catch (err) {
          resolvePending(null);
        }
      });

      pendingPromiseRef.current = tokenPromise;
      return tokenPromise;
    },
    [resolvePending, waitForCaptchaReady]
  );

  useEffect(() => {
    return () => {
      isReadyRef.current = false;
      resolveReady(null);
      resolvePending(null);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resolvePending, resolveReady]);

  const isValidCaptcha =
    CAPTCHA_DISABLED || (!loading && Boolean(captchaToken));

  const handlers = useMemo(
    () => ({
      onResolved,
      onExpired,
      onError,
      onLoaded,
      reset
    }),
    [onResolved, onExpired, onError, onLoaded, reset]
  );

  return {
    captchaToken,
    loading,
    captchaRef,
    isValidCaptcha,
    handlers,
    getToken
  };
};
